/**
 * การ์ด "หนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ)" ฝั่งพนักงาน — ใน /me/letters
 *
 * fb#176 (TN Riches 2 ต.ค. 2569): ลูกค้าถามว่าพนักงานขอใบ 50 ทวิ เองในระบบได้ไหม
 * คำตอบ: ได้สำหรับปีที่ปิดงวดธันวาคมแล้ว หรือเมื่อพ้นสภาพ — ระหว่างปีให้ HR ออกจากเมนูปลายปี
 * (เลขที่เอกสาร/วันที่ออกถูกตรึงตั้งแต่ครั้งแรกที่ออก ยอดระหว่างปียังเปลี่ยน จึงไม่ให้เจ้าตัวกดเอง)
 * กติกาตัดสินอยู่ที่ worker (/me/tawi50) จอแค่แสดงผล
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Receipt } from 'lucide-react';
import { apiDownload, apiFetch } from '../lib/api-client';
import { useToast } from './ui/toast';

interface MyTawi50Year {
  year: number;
  eligible: boolean;
  reason?: string;
}

export function MyTawi50Card() {
  const q = useQuery({
    queryKey: ['me', 'tawi50'],
    queryFn: () => apiFetch<{ years: MyTawi50Year[] }>('/me/tawi50').then((d) => d.years),
    staleTime: 60_000,
  });
  const toast = useToast();
  const [busyYear, setBusyYear] = useState<number | null>(null);

  async function download(year: number) {
    setBusyYear(year);
    try {
      await apiDownload(`/me/tawi50/pdf?year=${year}`, `50tawi-${year + 543}.pdf`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ดาวน์โหลดไม่สำเร็จ');
    } finally {
      setBusyYear(null);
    }
  }

  const years = q.data ?? [];

  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 font-bold">
        <Receipt className="w-5 h-5 text-teal" />
        หนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ)
      </div>
      <p className="text-xs text-gray-500 mt-1">
        ใช้ยื่นภาษีประจำปี ดาวน์โหลดได้เองเมื่อฝ่ายบุคคลปิดงวดเดือนธันวาคมของปีนั้นแล้ว หรือเมื่อพ้นสภาพพนักงาน
      </p>

      {q.isLoading ? (
        <div className="h-10 bg-gray-100 rounded mt-3 animate-pulse" aria-label="กำลังโหลด" />
      ) : q.error ? (
        <div className="mt-3 text-sm text-red-700">
          โหลดรายการไม่สำเร็จ{' '}
          <button onClick={() => void q.refetch()} className="underline font-medium">ลองใหม่</button>
        </div>
      ) : years.length === 0 ? (
        <div className="mt-3 text-sm text-gray-500">ยังไม่มียอดรายได้สะสมในระบบ — ใบ 50 ทวิ จะออกได้เมื่อมีการทำเงินเดือนผ่านระบบแล้ว</div>
      ) : (
        <div className="mt-3 divide-y divide-gray-100">
          {years.map((y) => (
            <div key={y.year} className="py-2.5 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium">ปีภาษี {y.year + 543}</div>
                {!y.eligible && y.reason && <div className="text-xs text-amber-700 mt-0.5">{y.reason}</div>}
              </div>
              <button
                onClick={() => download(y.year)}
                disabled={!y.eligible || busyYear !== null}
                title={y.eligible ? 'ดาวน์โหลด PDF' : y.reason}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50 min-h-[44px]"
              >
                <Download className="w-4 h-4" />
                {busyYear === y.year ? 'กำลังโหลด…' : 'ดาวน์โหลด'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
